import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useLanguage } from '../../../contexts/LanguageContext';

const SignatureWorkflowModal = ({ isOpen, onClose, currentLanguage }) => {
  const { t } = useLanguage();
  const [workflowName, setWorkflowName] = useState('');
  const [selectedDocument, setSelectedDocument] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [deadline, setDeadline] = useState('');
  const [signers, setSigners] = useState([{ name: '', email: '' }]);

  if (!isOpen) return null;

  const documentOptions = [
    { value: 'contract-omra', label: currentLanguage === 'fr' ? 'Contrat Omra - Groupe A' : 'عقد العمرة - المجموعة أ' },
    { value: 'attestation-hajj', label: currentLanguage === 'fr' ? 'Attestation Hajj' : 'شهادة الحج' },
    { value: 'liste-officielle', label: currentLanguage === 'fr' ? 'Liste Officielle' : 'القائمة الرسمية' }
  ];

  const groupOptions = [
    { value: 'omra-2026-01', label: 'Omra Janvier 2026 - Groupe A' },
    { value: 'omra-2026-02', label: 'Omra Février 2026 - Groupe B' },
    { value: 'hajj-2026', label: 'Hajj 2026 - Groupe Principal' }
  ];

  const handleAddSigner = () => {
    setSigners([...signers, { name: '', email: '' }]);
  };

  const handleRemoveSigner = (index) => {
    const newSigners = signers?.filter((_, i) => i !== index);
    setSigners(newSigners);
  };

  const handleSignerChange = (index, field, value) => {
    const newSigners = [...signers];
    newSigners[index][field] = value;
    setSigners(newSigners);
  };

  const handleCreate = () => {
    console.log('Creating workflow:', {
      workflowName,
      selectedDocument,
      selectedGroup,
      deadline,
      signers
    });
    
    alert(currentLanguage === 'fr' 
      ? `Workflow "${workflowName}" créé avec succès` 
      : `تم إنشاء سير العمل "${workflowName}" بنجاح`);
    
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" 
      onClick={onClose}
    >
      <div 
        className="bg-card rounded-lg border border-border shadow-warm max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="PenTool" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {t('documents.newSignatureWorkflow')}
              </h2>
              <p className="caption text-muted-foreground">
                {t('documents.configureSignature')}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            aria-label={t('documents.close')}
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <Input
            label={t('documents.workflowName')}
            placeholder={t('documents.workflowNamePlaceholder')}
            value={workflowName}
            onChange={(e) => setWorkflowName(e?.target?.value)}
            required
          />

          <Select
            label={t('documents.documentToSign')}
            options={documentOptions}
            value={selectedDocument}
            onChange={setSelectedDocument}
            required
          />

          <Select
            label={t('documents.pilgrimGroup')}
            options={groupOptions}
            value={selectedGroup}
            onChange={setSelectedGroup}
            searchable
            required
          />

          <Input
            label={t('documents.deadlineDate')}
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e?.target?.value)}
            required
          />

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-foreground">
                {t('documents.signers')}
              </label>
              <Button
                variant="outline"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                onClick={handleAddSigner}
              >
                {t('documents.add')}
              </Button>
            </div>

            <div className="space-y-3">
              {signers?.map((signer, index) => (
                <div key={index} className="flex gap-3 items-start p-4 bg-muted/30 rounded-lg">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      placeholder={t('documents.fullName')}
                      value={signer?.name}
                      onChange={(e) => handleSignerChange(index, 'name', e?.target?.value)}
                    />
                    <Input
                      type="email"
                      placeholder={t('documents.email')}
                      value={signer?.email}
                      onChange={(e) => handleSignerChange(index, 'email', e?.target?.value)}
                    />
                  </div>
                  {signers?.length > 1 && (
                    <button
                      onClick={() => handleRemoveSigner(index)}
                      className="p-2 text-destructive hover:bg-destructive/10 rounded-md transition-smooth"
                      aria-label={t('documents.remove')}
                    >
                      <Icon name="Trash2" size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div className="text-sm text-foreground">
                <p className="font-medium mb-1">
                  {t('documents.signatureProcess')}
                </p>
                <p className="text-muted-foreground">
                  {t('documents.signatureProcessInfo')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('documents.cancel')}
          </Button>
          <Button
            variant="default"
            iconName="Check"
            iconPosition="left"
            onClick={handleCreate}
            disabled={!workflowName || !selectedDocument || !selectedGroup || !deadline}
          >
            {t('documents.createWorkflow')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignatureWorkflowModal;